import { Component, EventEmitter, Inject, Input, OnChanges, Output } from '@angular/core';
import { MatChipListbox, MatChipsModule } from '@angular/material/chips';
import { Participant } from '../../interfaces/participant';
import { Recipient } from '../../interfaces/recipient';
import { Donor } from '../../interfaces/donor';
import { CommonModule } from '@angular/common';
import { Pair } from '../../interfaces/pair';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-warning-chips',
  imports: [MatChipsModule, MatIconModule, CommonModule, MatTooltipModule],
  templateUrl: './warning-chips.component.html',
  styleUrl: './warning-chips.component.scss'
})
export class WarningChipsComponent implements OnChanges {
  @Input() readonly participant!: Participant;
  @Input() recipient?: Recipient;
  @Input() donor?: Donor;
  @Input() pair?: Pair;
  @Input() isPanelOpen: boolean = false;
  @Input() pairs?: Pair[]; // Array of all pairs for multi-donor scenarios
  @Input() canDismissMultipleDonorWarning: boolean = false; // Whether user can dismiss the warning
  @Input() canDismissAcceptableBloodTypesWarning: boolean = false; // Whether user can dismiss the acceptable blood types warning
  @Input() canDismissMaxDonorAgeWarning: boolean = false; // Whether user can dismiss the max donor age warning
  @Input() canDismissMaxMismatchesWarning: boolean = false; // Whether user can dismiss the max mismatches warning
  @Output() pairUpdated = new EventEmitter<Pair>();
  @Output() multipleDonorWarningClicked = new EventEmitter<Recipient>();
  @Output() acceptableBloodTypesWarningClicked = new EventEmitter<Recipient>();
  @Output() maxDonorAgeWarningClicked = new EventEmitter<Recipient>();
  @Output() maxMismatchesWarningClicked = new EventEmitter<Recipient>();

  constructor() {}

  ngOnChanges(): void {}

  getPrivileges(): boolean {
    if (this.recipient)
      if (
        this.isUnder18(this.recipient.dob) ||
        this.recipient?.acceptableBloodTypes ||
        this.recipient?.acceptableHla ||
        this.recipient?.maxDonorAge ||
        this.recipient?.maxMismatches ||
        this.recipient?.maxMmA ||
        this.recipient?.maxMmB ||
        this.recipient?.maxMmDR ||
        this.recipient?.extraWaitTime ||
        this.recipient?.lowMfi1 ||
        this.recipient?.lowMfi2 ||
        this.hasMultipleDonors()
      ) {
        return true;
      }
    return false;
  }

  isUnder18(dob: string): boolean {
    const birthDate = new Date(dob);
    const today = new Date();

    // Calculate the age
    const age = today.getFullYear() - birthDate.getFullYear();

    // Check if the birthday has occurred this year
    const hasBirthdayPassed = today.getMonth() > birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

    // If birthday hasn't passed yet, subtract one year
    return hasBirthdayPassed ? age < 18 : age - 1 < 18;
  }

  hasUnknownCenter(participant: Recipient | Donor): boolean {
    return participant?.center?.name === 'UNKNOWN';
  }

  // Check if recipient has multiple donors
  hasMultipleDonors(): boolean {
    if (!this.recipient || !this.pairs) return false;
    return this.pairs.filter((pair) => pair.recipient.id === this.recipient!.id).length > 1;
  }

  // Check if recipient has outdated screening (older than 151 days)
  hasOutdatedScreening(): boolean {
    if (!this.recipient || !this.recipient.dateScreening) {
      return false;
    }

    if (this.recipient.selectedForTransplantation || this.recipient.transplanted) {
      return false;
    }

    const screeningDate = new Date(this.recipient.dateScreening);
    const today = new Date();
    const daysDifference = Math.floor((today.getTime() - screeningDate.getTime()) / (1000 * 60 * 60 * 24));

    return daysDifference > 151;
  }

  // Generate tooltip text for recipient privileges
  getRecipientPrivilegesText(): string {
    if (!this.recipient) return '';

    const privileges: string[] = [];

    if (this.isUnder18(this.recipient.dob)) {
      privileges.push('Child: extra wait time');
    }

    if (this.recipient.acceptableBloodTypes) {
      const formattedBloodTypes = this.recipient.acceptableBloodTypes.replace(/\s+/g, ', ');
      privileges.push(`Acceptable blood types: ${formattedBloodTypes}`);
    }

    if (this.recipient.acceptableHla) {
      privileges.push(`Acceptable HLA: ${this.recipient.acceptableHla}`);
    }

    if (this.recipient.maxDonorAge) {
      privileges.push(`Max donor age: ${this.recipient.maxDonorAge}`);
    }

    if (this.recipient.maxMismatches) {
      privileges.push(`Max mismatches: ${this.recipient.maxMismatches}`);
    }

    if (this.recipient.maxMmA) {
      privileges.push(`Max MM A: ${this.recipient.maxMmA}`);
    }

    if (this.recipient.maxMmB) {
      privileges.push(`Max MM B: ${this.recipient.maxMmB}`);
    }

    if (this.recipient.maxMmDR) {
      privileges.push(`Max MM DR: ${this.recipient.maxMmDR}`);
    }

    if (this.recipient.extraWaitTime) {
      privileges.push(`Committee: Extra wait time`);
    }

    if (this.recipient.lowMfi1) {
      privileges.push('Low MFI 1');
    }

    if (this.recipient.lowMfi2) {
      privileges.push('Low MFI 2');
    }

    if (this.hasMultipleDonors()) {
      privileges.push('Multiple donors');
    }

    return privileges.length > 0 ? privileges.join('\n') : '';
  }

  // Generate tooltip text for donor privileges (limited to centers)
  getDonorPrivilegesText(): string {
    if (!this.donor || !this.donor.limitedToCenter || this.donor.limitedToCenter.length === 0) {
      return '';
    }

    const centerNames = this.donor.limitedToCenter.map(center => center.name).join(', ');
    return `Limited to centers: ${centerNames}`;
  }

  // Generate tooltip text for priority
  getPriorityTooltipText(): string {
    if (!this.recipient || !this.recipient.priority) return '';
    return 'This recipient has priority status';
  }

  // Check if recipient is awaiting ENIS sync
  isRecipientAwaitingEnisSync(): boolean {
    return this.recipient ? (!this.recipient.firstName && !this.recipient.lastName) : false;
  }

  // Check if donor is awaiting ENIS sync
  isDonorAwaitingEnisSync(): boolean {
    return this.donor ? (!this.donor.firstName && !this.donor.lastName) : false;
  }

  // Check if multiple donor warning should be shown
  shouldShowMultipleDonorWarning(): boolean {
    if (!this.recipient || this.recipient.multipleDonorWarningDismissed) {
      return false;
    }

    // Only show warning after ENIS sync (firstName and lastName must exist)
    const enisSyncDone = !this.isRecipientAwaitingEnisSync();
    if (!enisSyncDone) {
      return false;
    }

    // Check if there are multiple donors
    const multipleDonors = this.hasMultipleDonors();
    if (!multipleDonors) {
      return false;
    }

    // Check if dateFirstDialysis is missing OR less than 2 years ago
    if (!this.recipient.dateFirstDialysis) {
      return true; // Missing dateFirstDialysis
    }

    const dialysisDate = new Date(this.recipient.dateFirstDialysis);
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

      // If dialysis date is MORE RECENT (later) than 2 years ago, show warning
    return dialysisDate >= twoYearsAgo;
  }

  // Handle click on multiple donor warning chip
  onMultipleDonorWarningClick(event: Event): void {
    event.stopPropagation(); // Prevent panel from expanding/collapsing
    if (this.recipient) {
      this.multipleDonorWarningClicked.emit(this.recipient);
    }
  }

  // Check if acceptable blood types warning should be shown
  shouldShowAcceptableBloodTypesWarning(): boolean {
    if (!this.recipient || this.recipient.acceptableBloodTypesWarningDismissed) {
      return false;
    }

    // Only show warning if acceptable blood types privilege exists
    if (!this.recipient.acceptableBloodTypes || this.recipient.acceptableBloodTypes.trim() === '') {
      return false;
    }

    // Only show warning after ENIS sync (firstName and lastName must exist)
    const enisSyncDone = !this.isRecipientAwaitingEnisSync();
    if (!enisSyncDone) {
      return false;
    }

    // Check if dateFirstDialysis is missing OR less than 2 years ago
    if (!this.recipient.dateFirstDialysis) {
      return true; // Missing dateFirstDialysis
    }

    const dialysisDate = new Date(this.recipient.dateFirstDialysis);
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

    // If dialysis date is MORE RECENT (later) than 2 years ago, show warning
    return dialysisDate >= twoYearsAgo;
  }

  // Handle click on acceptable blood types warning chip
  onAcceptableBloodTypesWarningClick(event: Event): void {
    event.stopPropagation(); // Prevent panel from expanding/collapsing
    if (this.recipient) {
      this.acceptableBloodTypesWarningClicked.emit(this.recipient);
    }
  }

  // Generate tooltip text for multiple donors warning
  getMultipleDonorsWarningTooltipText(): string {
    return "Check: Multiple donors: 'Patient has multiple donors, but missing or <2 y dialysis'";
  }

  // Generate tooltip text for acceptable blood types warning
  getAcceptableBloodTypesWarningTooltipText(): string {
    return "Check: Acceptables: 'Patient has acceptable bloodtypes, but missing or <2 y dialysis'";
  }

  // Check if max donor age warning should be shown
  shouldShowMaxDonorAgeWarning(): boolean {
    if (!this.recipient || this.recipient.maxDonorAgeWarningDismissed) {
      return false;
    }

    // Only show warning if max donor age privilege exists
    if (!this.recipient.maxDonorAge) {
      return false;
    }

    // Only show warning after ENIS sync (firstName and lastName must exist)
    const enisSyncDone = !this.isRecipientAwaitingEnisSync();
    if (!enisSyncDone) {
      return false;
    }

    // Check if recipient is part of a compatible pair
    if (this.pair && this.pair.compatible) {
      return false; // Don't show warning if in compatible pair
    }

    // Check if recipient is younger than 18
    if (this.recipient.dob && this.isUnder18(this.recipient.dob)) {
      return false; // Don't show warning if under 18
    }

    // Show warning if not in compatible pair AND not under 18
    return true;
  }

  // Handle click on max donor age warning chip
  onMaxDonorAgeWarningClick(event: Event): void {
    event.stopPropagation(); // Prevent panel from expanding/collapsing
    if (this.recipient) {
      this.maxDonorAgeWarningClicked.emit(this.recipient);
    }
  }

  // Generate tooltip text for max donor age warning
  getMaxDonorAgeWarningTooltipText(): string {
    return "Patient is ≥18 and not in a compatible pair";
  }

  // Check if max mismatches warning should be shown
  shouldShowMaxMismatchesWarning(): boolean {
    if (!this.recipient || this.recipient.maxMismatchesWarningDismissed) {
      return false;
    }

    // Only show warning if any of the max mismatches privileges exist
    const hasMaxMismatchesPrivilege = !!(
      this.recipient.maxMismatches ||
      this.recipient.maxMmA ||
      this.recipient.maxMmB ||
      this.recipient.maxMmDR
    );

    if (!hasMaxMismatchesPrivilege) {
      return false;
    }

    // Only show warning after ENIS sync (firstName and lastName must exist)
    const enisSyncDone = !this.isRecipientAwaitingEnisSync();
    if (!enisSyncDone) {
      return false;
    }

    // Check if recipient is part of a compatible pair
    if (this.pair && this.pair.compatible) {
      return false; // Don't show warning if in compatible pair
    }

    // Check if recipient is younger than 18
    if (this.recipient.dob && this.isUnder18(this.recipient.dob)) {
      return false; // Don't show warning if under 18
    }

    // Show warning if not in compatible pair AND not under 18
    return true;
  }

  // Handle click on max mismatches warning chip
  onMaxMismatchesWarningClick(event: Event): void {
    event.stopPropagation(); // Prevent panel from expanding/collapsing
    if (this.recipient) {
      this.maxMismatchesWarningClicked.emit(this.recipient);
    }
  }

  // Generate tooltip text for max mismatches warning
  getMaxMismatchesWarningTooltipText(): string {
    return "Patient is ≥18 and not in a compatible pair";
  }

  // ========== Combined Warning Methods (for when both recipient and donor have same warning) ==========

  // Check if both recipient and donor are awaiting ENIS sync
  bothAwaitingEnisSync(): boolean {
    return this.isRecipientAwaitingEnisSync() && this.isDonorAwaitingEnisSync();
  }

  // Check if only recipient is awaiting ENIS sync
  onlyRecipientAwaitingEnisSync(): boolean {
    return this.isRecipientAwaitingEnisSync() && !this.isDonorAwaitingEnisSync();
  }

  // Check if only donor is awaiting ENIS sync
  onlyDonorAwaitingEnisSync(): boolean {
    return this.isDonorAwaitingEnisSync() && !this.isRecipientAwaitingEnisSync();
  }

  // Get tooltip for combined ENIS sync warning
  getCombinedEnisSyncTooltip(): string {
    const lines: string[] = [];
    if (this.recipient && this.isRecipientAwaitingEnisSync()) {
      lines.push(`Recipient ${this.recipient.enisNumber}: ENIS sync needed`);
    }
    if (this.donor && this.isDonorAwaitingEnisSync()) {
      lines.push(`Donor ${this.donor.enisNumber}: ENIS sync needed`);
    }
    return lines.join('\n');
  }

  // Check if both have unknown center
  bothHaveUnknownCenter(): boolean {
    return this.recipient && this.donor ?
      this.hasUnknownCenter(this.recipient) && this.hasUnknownCenter(this.donor) : false;
  }

  // Check if only recipient has unknown center
  onlyRecipientHasUnknownCenter(): boolean {
    return this.recipient && (!this.donor || !this.hasUnknownCenter(this.donor)) ?
      this.hasUnknownCenter(this.recipient) : false;
  }

  // Check if only donor has unknown center
  onlyDonorHasUnknownCenter(): boolean {
    return this.donor && (!this.recipient || !this.hasUnknownCenter(this.recipient)) ?
      this.hasUnknownCenter(this.donor) : false;
  }

  // Get tooltip for combined unknown center warning
  getCombinedUnknownCenterTooltip(): string {
    const lines: string[] = [];
    if (this.recipient && this.hasUnknownCenter(this.recipient)) {
      lines.push(`Recipient ${this.recipient.enisNumber}: Assign Center`);
    }
    if (this.donor && this.hasUnknownCenter(this.donor)) {
      lines.push(`Donor ${this.donor.enisNumber}: Assign Center`);
    }
    return lines.join('\n');
  }

  // Check if both have missing DOB
  bothHaveMissingDob(): boolean {
    return this.recipient?.dob === '1900-01-01' && this.donor?.dob === '1900-01-01';
  }

  // Check if only recipient has missing DOB
  onlyRecipientHasMissingDob(): boolean {
    return this.recipient?.dob === '1900-01-01' && this.donor?.dob !== '1900-01-01';
  }

  // Check if only donor has missing DOB
  onlyDonorHasMissingDob(): boolean {
    return this.donor?.dob === '1900-01-01' && this.recipient?.dob !== '1900-01-01';
  }

  // Get tooltip for combined missing DOB warning
  getCombinedMissingDobTooltip(): string {
    const lines: string[] = [];
    if (this.recipient?.dob === '1900-01-01') {
      lines.push(`Recipient ${this.recipient.enisNumber}: Missing Date of Birth`);
    }
    if (this.donor?.dob === '1900-01-01') {
      lines.push(`Donor ${this.donor.enisNumber}: Missing Date of Birth`);
    }
    return lines.join('\n');
  }

  // Check if recipient has missing HLA
  recipientHasMissingHla(): boolean {
    return this.recipient ?
      ((!this.recipient.abPhenotype || this.recipient.abPhenotype === '') ||
       (!this.recipient.drPhenotype || this.recipient.drPhenotype === '')) : false;
  }

  // Check if donor has missing HLA
  donorHasMissingHla(): boolean {
    return this.donor ?
      (this.donor.hla === undefined || this.donor.hla === '') : false;
  }

  // Check if both have missing HLA
  bothHaveMissingHla(): boolean {
    return this.recipientHasMissingHla() && this.donorHasMissingHla();
  }

  // Check if only recipient has missing HLA
  onlyRecipientHasMissingHla(): boolean {
    return this.recipientHasMissingHla() && !this.donorHasMissingHla();
  }

  // Check if only donor has missing HLA
  onlyDonorHasMissingHla(): boolean {
    return this.donorHasMissingHla() && !this.recipientHasMissingHla();
  }

  // Get tooltip for combined missing HLA warning
  getCombinedMissingHlaTooltip(): string {
    const lines: string[] = [];
    if (this.recipient && this.recipientHasMissingHla()) {
      lines.push(`Recipient ${this.recipient.enisNumber}: Missing HLA`);
    }
    if (this.donor && this.donorHasMissingHla()) {
      lines.push(`Donor ${this.donor.enisNumber}: Missing HLA`);
    }
    return lines.join('\n');
  }

  // Check if recipient has missing blood type
  recipientHasMissingBloodType(): boolean {
    return this.recipient ?
      (!this.recipient.abo || this.recipient.abo === '') : false;
  }

  // Check if donor has missing blood type
  donorHasMissingBloodType(): boolean {
    return this.donor ?
      (!this.donor.abo || this.donor.abo === '') : false;
  }

  // Check if both have missing blood type
  bothHaveMissingBloodType(): boolean {
    return this.recipientHasMissingBloodType() && this.donorHasMissingBloodType();
  }

  // Check if only recipient has missing blood type
  onlyRecipientHasMissingBloodType(): boolean {
    return this.recipientHasMissingBloodType() && !this.donorHasMissingBloodType();
  }

  // Check if only donor has missing blood type
  onlyDonorHasMissingBloodType(): boolean {
    return this.donorHasMissingBloodType() && !this.recipientHasMissingBloodType();
  }

  // Get tooltip for combined missing blood type warning
  getCombinedMissingBloodTypeTooltip(): string {
    const lines: string[] = [];
    if (this.recipient && this.recipientHasMissingBloodType()) {
      lines.push(`Recipient ${this.recipient.enisNumber}: Missing Blood Type`);
    }
    if (this.donor && this.donorHasMissingBloodType()) {
      lines.push(`Donor ${this.donor.enisNumber}: Missing Blood Type`);
    }
    return lines.join('\n');
  }

  // Check if both have privileges
  bothHavePrivileges(): boolean {
    return this.getPrivileges() && !!(this.donor?.limitedToCenter && this.donor.limitedToCenter.length > 0);
  }

  // Check if only recipient has privileges
  onlyRecipientHasPrivileges(): boolean {
    return this.getPrivileges() && (!this.donor?.limitedToCenter || this.donor.limitedToCenter.length === 0);
  }

  // Check if only donor has privileges
  onlyDonorHasPrivileges(): boolean {
    return !!(this.donor?.limitedToCenter && this.donor.limitedToCenter.length > 0) && !this.getPrivileges();
  }

  // Get tooltip for combined privileges
  getCombinedPrivilegesTooltip(): string {
    const lines: string[] = [];
    if (this.recipient && this.getPrivileges()) {
      const recipientPrivileges = this.getRecipientPrivilegesText();
      lines.push(`Recipient ${this.recipient.enisNumber}:\n${recipientPrivileges}`);
    }
    if (this.donor && this.donor.limitedToCenter && this.donor.limitedToCenter.length > 0) {
      const donorPrivileges = this.getDonorPrivilegesText();
      lines.push(`Donor ${this.donor.enisNumber}:\n${donorPrivileges}`);
    }
    return lines.join('\n\n');
  }

  // Check if both are locked
  bothAreLocked(): boolean {
    return !!this.recipient?.locked && !!this.donor?.locked;
  }

  // Check if only recipient is locked
  onlyRecipientIsLocked(): boolean {
    return !!this.recipient?.locked && !this.donor?.locked;
  }

  // Check if only donor is locked
  onlyDonorIsLocked(): boolean {
    return !!this.donor?.locked && !this.recipient?.locked;
  }

  // Get tooltip for combined locked status
  getCombinedLockedTooltip(): string {
    const lines: string[] = [];
    if (this.recipient?.locked) {
      lines.push(`Recipient ${this.recipient.enisNumber}: Locked`);
    }
    if (this.donor?.locked) {
      lines.push(`Donor ${this.donor.enisNumber}: Locked`);
    }
    return lines.join('\n');
  }

  // Check if both are selected for transplantation
  bothAreSelected(): boolean {
    return !!(this.recipient?.selectedForTransplantation && !this.recipient?.transplanted) &&
           !!(this.donor?.selectedForTransplantation && !this.donor?.transplanted);
  }

  // Check if only recipient is selected
  onlyRecipientIsSelected(): boolean {
    return !!(this.recipient?.selectedForTransplantation && !this.recipient?.transplanted) &&
           !(this.donor?.selectedForTransplantation && !this.donor?.transplanted);
  }

  // Check if only donor is selected
  onlyDonorIsSelected(): boolean {
    return !!(this.donor?.selectedForTransplantation && !this.donor?.transplanted) &&
           !(this.recipient?.selectedForTransplantation && !this.recipient?.transplanted);
  }

  // Get tooltip for combined selected status
  getCombinedSelectedTooltip(): string {
    const lines: string[] = [];
    if (this.recipient?.selectedForTransplantation && !this.recipient?.transplanted) {
      lines.push(`Recipient ${this.recipient.enisNumber}: Selected for transplantation`);
    }
    if (this.donor?.selectedForTransplantation && !this.donor?.transplanted) {
      lines.push(`Donor ${this.donor.enisNumber}: Selected for transplantation`);
    }
    return lines.join('\n');
  }

  // Check if both are transplanted
  bothAreTransplanted(): boolean {
    return !!this.recipient?.transplanted && !!this.donor?.transplanted;
  }

  // Check if only recipient is transplanted
  onlyRecipientIsTransplanted(): boolean {
    return !!this.recipient?.transplanted && !this.donor?.transplanted;
  }

  // Check if only donor is transplanted
  onlyDonorIsTransplanted(): boolean {
    return !!this.donor?.transplanted && !this.recipient?.transplanted;
  }

  // Get tooltip for combined transplanted status
  getCombinedTransplantedTooltip(): string {
    const lines: string[] = [];
    if (this.recipient?.transplanted) {
      lines.push(`Recipient ${this.recipient.enisNumber}: Transplanted`);
    }
    if (this.donor?.transplanted) {
      lines.push(`Donor ${this.donor.enisNumber}: Transplanted`);
    }
    return lines.join('\n');
  }

}
